import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Col, Row } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useStateMachine } from "little-state-machine";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as NoEntry } from "assets/images/iconsV2/no-entry.svg";
import {
  cancelQuickRequest,
  cancelQuoteRequest,
  cancelShipment,
  cancelAndDuplicateShipment,
  requestQuickRequestCancellation,
} from "api";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import Button from "components/v2/Button/Button";
import Textarea from "components/atoms/Textarea/Textarea";
import { ReactComponent as SlashCircle } from "assets/images/iconsV2/slash-circle-red.svg";
import Alert from "components/v2/Alert/Alert";
import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { quickRequestQueryKeys } from "api/queries/quick-request/quickRequestQueryKeys";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { updateBackgroundLoadingState } from "store/store";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import Modal from "components/v2/Modal/Modal";
import Dropdown from "components/v2/Dropdown/Dropdown";
import CANCEL_REQUEST_REASON_OPTIONS from "./shipper/CancelRequestReasonOptions";

/*
Checks:
1. If user can cancel a quote request OR if a user can request a cancellation and the request is a quick request: show main cancellation dialog
2. If user logged in is equal to the user who created the quote request: show main cancellation dialog
3. If user cannot cancel a quote request but the user can submit a request to cancel a quote AND the quote has not already been requested to cancel: show request to cancel dialog
4. If user cannot cancel a quote request but the user can submit a request to cancel a quote AND the quote has already been requested to cancel: show "Already requested to cancel" alert
5. If user cannot cancel a quote and cannot request to cancel a quote: show "No permission to cancel" alert
*/

const CancelRequestDialog = function ({
  close,
  isOpen,
  isQuickRequest,
  isShipment,
  isShipmentCancelAndDuplicate,
  shipmentId,
  createdByEmail,
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { actions } = useStateMachine({ updateBackgroundLoadingState });
  const methods = useForm({
    shouldFocusError: true,
  });
  const { getValues, handleSubmit } = methods;
  const { permissions } = usePermissions();
  const { edit_other_quote_requests, create_quick_requests, create_quote_requests, view_quick_requests } =
    JSON.parse(permissions).permissions.quoting;
  const { email } = JSON.parse(permissions);
  // if email and createdByEmail are the exact same AND if create_quote_requests is TRUE, then isOwnQuoteRequest is TRUE and a user can cancel their own quote request
  const isOwnQuoteRequest = email === createdByEmail && create_quote_requests;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: quickDetails, refetch: quickDetailRefetch } = useGetQuickRequestDetails(shipmentId, {
    enabled: !!shipmentId && isQuickRequest && isOpen,
    select: (data) => data.organization,
  });
  const { data: quoteDetails, refetch: quoteDetailRefetch } = useGetQuoteRequestDetails({
    shipmentId,
    options: {
      enabled: !!shipmentId && !isQuickRequest && isOpen,
      select: (data) => data.organization,
    },
  });
  const requestDetails = useMemo(
    () => (isQuickRequest ? quickDetails : quoteDetails) || {},
    [isQuickRequest, quickDetails, quoteDetails],
  );
  const refetchDetails = isQuickRequest ? quickDetailRefetch : quoteDetailRefetch;

  const { can_request_to_cancel_quote_request: canRequestCancellation, has_requested_cancellation } = requestDetails;

  const canCancelQuickRequest = create_quick_requests || (view_quick_requests && create_quote_requests);

  const noPermission =
    (isQuickRequest && !canCancelQuickRequest) ||
    (!isQuickRequest && !edit_other_quote_requests && !canRequestCancellation && !isOwnQuoteRequest);

  /**
   * Retreive the request details when the dialog is opened
   */
  useEffect(() => {
    if (isOpen) refetchDetails();
  }, [isOpen]);

  /**
   * Cancel a quick request, quote request, or shipment via the API
   */
  const cancelRequest = () => {
    setIsSubmitting(true);
    // determine which API call to make based on whether it's a quick request, quote request, or shipment
    let cancelRequestFn;
    if (isShipment) {
      if (isShipmentCancelAndDuplicate) {
        cancelRequestFn = cancelAndDuplicateShipment;
      } else {
        cancelRequestFn = cancelShipment;
      }
    } else if (isQuickRequest) cancelRequestFn = cancelQuickRequest;
    else cancelRequestFn = cancelQuoteRequest;
    // make the API call to cancel the request
    const payload = {
      reason: getValues("reason_for_canceling"),
      comment: getValues("cancel_feedback"),
    };
    cancelRequestFn(shipmentId, payload)
      .then((res) => {
        if (res.status === 200) {
          handleRequestCancel();
          close();

          if (isShipmentCancelAndDuplicate && res.data) {
            // redirect to the duplicate shipment page
            if (res.data.type === "shipment") {
              navigate(`/shipment/${res.data.reference_number}?cancel_duplicate=${shipmentId}&pull_quotes=true`);
            } else if (res.data.type === "draft") {
              navigate(`/quote-request?draft_id=${res.data.reference_number}&is_duplicated=true&active_step=provider`);
            }
          }
        }
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  /**
   * Request cancellation for a quick request
   */
  const requestCancellation = () => {
    setIsSubmitting(true);
    // determine which API call to make based on whether the user can request a cancellation or not
    const cancelFn = canRequestCancellation ? requestQuickRequestCancellation : cancelQuickRequest;
    cancelFn(shipmentId)
      .then((res) => {
        if (res.status === 200) {
          handleRequestCancel();
          close();
          toast("Cancellation request successfully submitted", { type: "success" });
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "shipment-quotes.components.cancelRequestDialog.couldNotRequestCancel",
            defaultMessage: "Could not request. Please try again later.",
          }),
        ),
      )
      .finally(() => setIsSubmitting(false));
  };

  const handleRequestCancel = () => {
    actions.updateBackgroundLoadingState(true);
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentBasicDetails(shipmentId) });
    queryClient.invalidateQueries({
      queryKey: isQuickRequest
        ? quickRequestQueryKeys.quickRequestDetails(shipmentId)
        : quoteRequestQueryKeys.quoteDetails(shipmentId),
    });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentQuotationActionHistoryDetails(shipmentId) });
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteProviderOptionsListing(shipmentId) });
    queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(shipmentId) });
    queryClient.invalidateQueries({ queryKey: shipmentQueryKeys.shipmentDetails(shipmentId) });
    actions.updateBackgroundLoadingState(false);
  };

  const getCancelRequestTitle = () => {
    if (has_requested_cancellation) {
      return intl.formatMessage({
        id: "tracking.cancellationAlreadySubmitted",
        defaultMessage: "Cancellation request already submitted",
      });
    }
    if (isShipment) {
      if (isShipmentCancelAndDuplicate) {
        return intl.formatMessage({
          id: "tracking.cancelShipmentAndDuplicate",
          defaultMessage: "Cancel and book another provider",
        });
      }
      return intl.formatMessage({
        id: "tracking.cancelShipment",
        defaultMessage: "Cancel shipment?",
      });
    }
    if (canRequestCancellation) {
      return intl.formatMessage({
        id: "quick-request.requestCancellationTitle",
        defaultMessage: "Request cancellation of this quote request?",
      });
    }
    return intl.formatMessage({
      id: isQuickRequest
        ? "quick-request.cancelRequest"
        : "shipment-quotes.components.cancelRequestBanner.cancelQuoteRequest",
      defaultMessage: isQuickRequest ? "Cancel quick request?" : "Cancel quote request?",
    });
  };

  if (isShipmentCancelAndDuplicate)
    return (
      <Modal show={isOpen} onHide={close} size="xl">
        <Modal.Header>
          <div className="p-3 bg-error-secondary width-fit radius-full">
            <NoEntry className="" />
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="text-primary text-xl font-semibold mt-3">{getCancelRequestTitle()}</div>
          <div className="text-primary text-sm fg-tertiary my-3">
            <FormattedMessage
              id="tracking.cancelShipmentAndDuplicateSubtitle"
              defaultMessage="The provider will be notified of the cancelation, and you will need to rebook with another provider."
            />
          </div>
          <Alert variant="warning">
            <FormattedMessage
              id="tracking.cancelShipmentAndDuplicateWarning"
              defaultMessage="This will duplicate the quote request."
            />
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex align-items-center justify-content-end">
            <div className="mr-lg">
              <Button
                label={intl.formatMessage({ id: "general.back", defaultMessage: "Back" })}
                onClick={close}
                variant="secondary-gray"
                square
                className="py-3 font-semibold"
              />
            </div>
            <Button
              label={intl.formatMessage({
                id: "tracking.cancelShipmentAndDuplicateConfirmation",
                defaultMessage: "Yes, cancel and rebook",
              })}
              onClick={handleSubmit(cancelRequest)}
              disabled={isSubmitting}
              square
              variant="destructive-primary"
              className="py-3 font-semibold"
            />
          </div>
        </Modal.Footer>
      </Modal>
    );

  return (
    <FormProvider {...methods}>
      <Modal show={isOpen} onHide={close} size="md">
        <Modal.Header>
          <div className="d-flex flex-column">
            {!has_requested_cancellation && (
              <div
                style={{ width: 48, height: 48 }}
                className="d-flex justify-content-center align-items-center p-lg shadow-xs radius-full bg-error-secondary"
              >
                <SlashCircle />
              </div>
            )}
            <div className="mt-xl text-primary text-xl font-semibold">{getCancelRequestTitle()}</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="mt-xs text-tertiary text-sm mb-3xl">
            {(edit_other_quote_requests || isOwnQuoteRequest) && !isQuickRequest ? (
              <form onSubmit={handleSubmit(cancelRequest)}>
                <div>
                  <FormattedMessage
                    id="tracking.cancelRequestPrompt"
                    defaultMessage="Providers will be notified of the cancellation, and you will no longer be able to select any quotes."
                  />
                </div>
                {!isShipment && (
                  <>
                    <Row className="mb-15 mt-2xl">
                      <Col lg={12}>
                        <label className="required text-sm font-medium mb-xs">
                          <FormattedMessage id="tracking.reasonForCanceling" defaultMessage="Reason for canceling" />
                        </label>
                        <Dropdown
                          options={CANCEL_REQUEST_REASON_OPTIONS(intl)}
                          name="reason_for_canceling"
                          eventContext="Cancel Request Dialog"
                          required
                          alignRight
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <div className="d-flex flex-column mb-3xl">
                          <label className="required text-sm font-medium mb-xs">
                            <FormattedMessage id="tracking.comment" defaultMessage="Comments" />
                          </label>
                          <Textarea
                            required
                            eventContext="Cancel Request Dialog"
                            name="cancel_feedback"
                            validations={{ minLength: { value: 3, message: "Please enter at least 3 characters" } }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </>
                )}
                <Row>
                  <Col xs={12} className="d-flex flex-row justify-content-end mt-lg">
                    <Button
                      size="lg"
                      variant="secondary-gray"
                      id="cancel-shipment-button"
                      label={intl.formatMessage({
                        id: "general.back",
                        defaultMessage: "Back",
                      })}
                      onClick={close}
                    />
                    <Button
                      size="lg"
                      variant="destructive-primary"
                      label={
                        isShipment
                          ? intl.formatMessage({
                              id: "shipment-quotes.components.cancelRequestBanner.cancelShipment",
                              defaultMessage: "Cancel Shipment",
                            })
                          : intl.formatMessage({
                              id: "shipment-quotes.components.cancelRequestBanner.cancelQuoteRequest",
                              defaultMessage: "Cancel quote request",
                            })
                      }
                      type="submit"
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                      data-testid={testIds.SHIPMENT_DASHBOARD_DELETE_CONFIRM_BUTTON}
                    />
                  </Col>
                </Row>
              </form>
            ) : null}
            {has_requested_cancellation && (
              <>
                <div className="mt-xl">
                  <FormattedMessage
                    id="tracking.cancellationAlreadySubmittedMessage"
                    defaultMessage="You cannot submit another cancellation request as one has already been submitted."
                  />
                </div>
                <div className="d-flex justify-content-end mt-2xl">
                  <Button
                    size="lg"
                    id="cancel-shipment-button"
                    style={{ float: "right" }}
                    label={intl.formatMessage({
                      id: "settings.provider.close",
                      defaultMessage: "Close",
                    })}
                    onClick={close}
                  />
                </div>
              </>
            )}
            {!has_requested_cancellation && !noPermission && isQuickRequest && (
              <>
                {canRequestCancellation ? (
                  <FormattedMessage
                    id="tracking.requestToCancel"
                    defaultMessage="Are you sure you want to request cancellation for this quote request?"
                  />
                ) : (
                  <>
                    {requestDetails?.quick_request_status === "Pending" ? (
                      <FormattedMessage
                        id="general.thisActionCannotBeUndone"
                        defaultMessage="This action cannot be undone."
                      />
                    ) : requestDetails?.quick_request_status === "Processing" ? (
                      <FormattedMessage
                        id="quick-request.cancelProcessingRequestMessage"
                        defaultMessage="This will send a cancellation request to your quote requestor, who will need to confirm the cancellation."
                      />
                    ) : (
                      <FormattedMessage
                        id="tracking.cancel"
                        defaultMessage="Are you sure you want to cancel this quick request?"
                      />
                    )}
                  </>
                )}

                <div xs={12} className="d-flex flex-row w-100 justify-content-end pt-22">
                  <Button
                    id="cancel-shipment-button"
                    variant="secondary-gray"
                    label={intl.formatMessage({
                      id: "general.back",
                      defaultMessage: "Back",
                    })}
                    size="lg"
                    onClick={close}
                  />
                  <Button
                    variant="destructive-primary"
                    size="lg"
                    label={
                      canRequestCancellation
                        ? intl.formatMessage({
                            id: "quick-request.requestCancellation",
                            defaultMessage: "Request cancellation",
                          })
                        : intl.formatMessage({
                            id: "billing.cancelUpdateDialog.yesCancel",
                            defaultMessage: "Yes, cancel",
                          })
                    }
                    onClick={requestCancellation}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  />
                </div>
              </>
            )}
            {noPermission && (
              <>
                <FormattedMessage
                  id={isQuickRequest ? "tracking.cancelQuickRequestDenied" : "tracking.cancelRequestDenied"}
                  defaultMessage={
                    isQuickRequest
                      ? "You do not have permission to cancel this quick request"
                      : "You do not have permission to cancel this quote request"
                  }
                />
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    id="cancel-shipment-button"
                    size="lg"
                    variant="secondary-gray"
                    label={intl.formatMessage({
                      id: "tracking.discard",
                      defaultMessage: "Discard",
                    })}
                    onClick={() => {
                      close();
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </FormProvider>
  );
};

CancelRequestDialog.propTypes = {
  close: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isQuickRequest: PropTypes.bool.isRequired,
  isShipment: PropTypes.bool.isRequired,
  isShipmentCancelAndDuplicate: PropTypes.bool.isRequired,
  shipmentId: PropTypes.string.isRequired,
  createdByEmail: PropTypes.string,
};

CancelRequestDialog.defaultProps = {
  createdByEmail: undefined,
};

export default CancelRequestDialog;
